import type { TransitionType } from "./effects";
import type { EmphasisAnimation } from "../graphics/types";
import type { StabilizationProfile } from "../video/stabilization/types";

export interface Timeline {
  readonly tracks: Track[];
  readonly subtitles: Subtitle[];
  readonly duration: number;
  readonly markers: Marker[];
  readonly beatMarkers?: TimelineBeatMarker[];
  readonly beatAnalysis?: TimelineBeatAnalysis;
  /**
   * Canvas letterbox fill behind clips whose aspect differs from the project
   * canvas. Raw values are part of the shared cross-platform project JSON
   * schema (identical on iOS/Android). `"color"` paints `layoutBackgroundColor`;
   * `"blur"` paints a blurred cover-fit copy of the base clip. Undefined keeps
   * the default black bars.
   *
   * The background belongs to the composition, not to the editor theme, so
   * every renderer must resolve these two fields through
   * `resolveCanvasBackgroundColor` / `canvasBackgroundNeedsBlurBackdrop` in
   * `video/canvas-background.ts`, which is the canonical definition. Filling a
   * rendered canvas from a CSS theme token makes the exported file depend on
   * light/dark mode and is always a bug.
   */
  readonly backgroundFillMode?: "color" | "blur";
  readonly layoutBackgroundColor?: string;
}

export interface TimelineBeatMarker {
  readonly time: number;
  readonly strength: number;
  readonly index: number;
  readonly isDownbeat: boolean;
}

export interface TimelineBeatAnalysis {
  readonly bpm: number;
  readonly confidence: number;
  readonly sourceClipId?: string;
  readonly analyzedAt: number;
}

export interface Track {
  readonly id: string;
  readonly type: "video" | "audio" | "image" | "text" | "graphics";
  readonly name: string;
  readonly clips: Clip[];
  readonly transitions: Transition[];
  readonly locked: boolean;
  readonly hidden: boolean;
  readonly muted: boolean;
  readonly solo: boolean;
  /**
   * Gain for everything on this track, multiplying each clip's own volume.
   * Optional because every project saved before track volume existed has no
   * such field, and those projects must keep playing at unity: read it through
   * `resolveTrackVolume` rather than treating a missing value as 0.
   *
   * Mute is a separate switch, not volume 0, so unmuting a track restores the
   * level it was set to.
   */
  readonly volume?: number;
  /** Tracks with the same id move and trim their related items together. */
  readonly groupId?: string;
}

/** Track gain shares the clip volume range: 0 is silence, 4 is +12 dB. */
export const TRACK_VOLUME_MIN = 0;
export const TRACK_VOLUME_MAX = 4;

export function clampTrackVolume(volume: number): number {
  if (!Number.isFinite(volume)) return 1;
  return Math.max(TRACK_VOLUME_MIN, Math.min(TRACK_VOLUME_MAX, volume));
}

/**
 * The one read site for `Track.volume`. Absent means unity gain, which is what
 * keeps projects written before this field from exporting silence.
 */
export function resolveTrackVolume(
  track: Pick<Track, "volume"> | null | undefined,
): number {
  const volume = track?.volume;
  return typeof volume === "number" ? clampTrackVolume(volume) : 1;
}

export interface EditingTemplateApplicationSource {
  readonly templateId: string;
  readonly applicationId: string;
  readonly ownerClipId: string;
  readonly ownerTrackId?: string;
  readonly controlValues?: Record<string, unknown>;
}

export interface AppliedEditingTemplate {
  readonly templateId: string;
  readonly applicationId: string;
  readonly name: string;
  readonly category?: string;
  readonly appliedAt: number;
  readonly controlValues?: Record<string, unknown>;
}

export interface ClipMetadata {
  readonly templateSource?: EditingTemplateApplicationSource;
  readonly appliedTemplates?: AppliedEditingTemplate[];
  readonly templateManaged?: boolean;
  readonly templateTrackType?: "text" | "graphics";
  /** Caption style preset this clip was generated with. */
  readonly captionPresetId?: string;
  /**
   * Per-word timings for karaoke rendering, in seconds **relative to the clip's
   * own start**. Clip-relative on purpose: dragging or trimming a caption then
   * leaves its word timings correct with no rewrite pass.
   */
  readonly captionWords?: readonly SubtitleWord[];
  /**
   * The animation actually in force. May be "none" even for an animated preset
   * when the transcript came back without word timings.
   */
  readonly captionAnimationStyle?: CaptionAnimationStyle;
  readonly captionHighlightColor?: string;
  /** Wrap width as a fraction of frame width, from the preset. */
  readonly captionMaxWidthRatio?: number;
  readonly [key: string]: unknown;
}

export interface EffectMetadata {
  readonly templateSource?: EditingTemplateApplicationSource;
  readonly [key: string]: unknown;
}

/**
 * keyColor channels are 0..1. tolerance, edgeSoftness and spillSuppression are
 * 0..1 on the scale defined by MAX_RGB_DISTANCE in video/chroma-key-engine,
 * which is the single convention every keying path obeys. Changing what these
 * numbers mean rewrites how every saved project looks, so change it there.
 */
export interface ChromaKeySettings {
  readonly enabled: boolean;
  readonly keyColor: { readonly r: number; readonly g: number; readonly b: number };
  readonly tolerance: number;
  readonly edgeSoftness: number;
  readonly spillSuppression: number;
}

export interface SpeedKeyframe {
  readonly id: string;
  readonly time: number;
  readonly speed: number;
  readonly easing: EasingType;
}

export interface FreezeFrame {
  readonly id: string;
  readonly clipId: string;
  readonly sourceTime: number;
  readonly startTime: number;
  readonly duration: number;
}

export interface Clip {
  readonly id: string;
  readonly mediaId: string;
  readonly trackId: string;
  readonly startTime: number;
  readonly duration: number;
  readonly inPoint: number;
  readonly outPoint: number;
  readonly effects: Effect[];
  readonly audioEffects: Effect[];
  readonly transform: Transform;
  readonly blendMode?: import("../video/types").BlendMode;
  readonly blendOpacity?: number;
  readonly colorGrading?: import("../video/color-grading-engine").ClipColorGrading;
  readonly volume: number;
  readonly fade?: { fadeIn: number; fadeOut: number };
  readonly automation?: {
    volume?: AutomationPoint[];
    pan?: AutomationPoint[];
  };
  readonly keyframes: Keyframe[];
  /**
   * Motion path this clip travels along. Authoring state only: it is baked into
   * `keyframes` by `applyMotionPathToKeyframes`, which is what preview and
   * export read. clip-motion-path.ts defines the coordinate and time meanings.
   */
  readonly motionPath?: import("../animation/clip-motion-path").ClipMotionPath;
  readonly speed?: number;
  readonly reversed?: boolean;
  readonly smoothSlowMo?: boolean;
  readonly interpolationQuality?: "low" | "medium" | "high";
  readonly stabilization?: {
    enabled: boolean;
    strength: number;
    cropMode: "auto" | "none";
    analyzed?: boolean;
    analysisVersion?: number;
    profile?: StabilizationProfile;
  };
  readonly emphasisAnimation?: EmphasisAnimation;
  readonly chromaKey?: ChromaKeySettings;
  /** Persisted AI background removal. The engine map is a session cache of this. */
  readonly backgroundRemoval?: import("../ai/background-removal-settings").BackgroundRemovalSettings;
  readonly speedKeyframes?: SpeedKeyframe[];
  readonly freezeFrames?: FreezeFrame[];
  readonly pitchCorrection?: boolean;
  /** Zero-based index of the audio track within the source media file to use for this clip.
   * Undefined or 0 means the primary/first audio track. */
  readonly audioTrackIndex?: number;
  readonly metadata?: ClipMetadata;
}

export interface Effect {
  readonly id: string;
  readonly type: string;
  readonly params: Record<string, unknown>;
  readonly enabled: boolean;
  readonly metadata?: EffectMetadata;
}

export type FitMode = "contain" | "cover" | "stretch" | "none";

export interface Transform {
  readonly position: { x: number; y: number };
  readonly scale: { x: number; y: number };
  readonly rotation: number;
  readonly anchor: { x: number; y: number };
  readonly opacity: number;
  readonly borderRadius?: number;
  readonly fitMode?: FitMode;
  readonly rotate3d?: { x: number; y: number; z: number };
  readonly perspective?: number;
  readonly transformStyle?: "flat" | "preserve-3d";
  readonly crop?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  /**
   * Normalized 0..1 frame rectangle this clip is confined to.
   *
   * Layout presets fill a slot with `fitMode: "cover"`, which by definition
   * draws past the slot edges whenever the clip's aspect differs from the
   * slot's. Without a clip region that overflow lands on top of neighbouring
   * slots. Set by layout presets; absent for ordinary clips, which are free to
   * use the whole frame.
   */
  readonly layoutSlot?: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
}

export interface Keyframe {
  readonly id: string;
  readonly time: number;
  readonly property: string;
  readonly value: unknown;
  readonly easing: EasingType;
  /**
   * Optional per-keyframe temporal bezier handles (graph editor). When set with
   * easing "bezier", the keyframe engine interpolates the OUTGOING segment using
   * these control points (in/out in normalized 0..1 time/value space), matching
   * After Effects' value-graph Bezier handles. Honored by keyframeEngine.applyEasing.
   */
  readonly bezierHandles?: {
    readonly in: { readonly x: number; readonly y: number };
    readonly out: { readonly x: number; readonly y: number };
  };
  readonly roving?: boolean;
}

export const EASING_TYPES = [
  "linear",
  "ease",
  "ease-in",
  "ease-out",
  "ease-in-out",
  "hold",
  "bezier",
  "smoothstep",
  "smootherstep",
  "snappy",
  "smooth",
  "easeInQuad",
  "easeOutQuad",
  "easeInOutQuad",
  "easeInCubic",
  "easeOutCubic",
  "easeInOutCubic",
  "easeInQuart",
  "easeOutQuart",
  "easeInOutQuart",
  "easeInQuint",
  "easeOutQuint",
  "easeInOutQuint",
  "easeInSine",
  "easeOutSine",
  "easeInOutSine",
  "easeInExpo",
  "easeOutExpo",
  "easeInOutExpo",
  "easeInCirc",
  "easeOutCirc",
  "easeInOutCirc",
  "easeInBack",
  "easeOutBack",
  "easeInOutBack",
  "easeInBackStrong",
  "easeOutBackStrong",
  "easeInOutBackStrong",
  "easeInElastic",
  "easeOutElastic",
  "easeInOutElastic",
  "easeInElasticSoft",
  "easeOutElasticSoft",
  "easeInOutElasticSoft",
  "easeInBounce",
  "easeOutBounce",
  "easeInOutBounce",
  "easeInBounceSmall",
  "easeOutBounceSmall",
  "easeInOutBounceSmall",
] as const;

export type EasingType = (typeof EASING_TYPES)[number];

export interface Marker {
  readonly id: string;
  readonly time: number;
  readonly label: string;
  readonly color: string;
}

export type TransitionEdge = "in" | "out";

export interface Transition {
  readonly id: string;
  readonly clipAId: string;
  readonly clipBId?: string;
  readonly edge?: TransitionEdge;
  readonly type: TransitionType;
  readonly duration: number;
  readonly params: Record<string, unknown>;
}

export type CaptionAnimationStyle =
  | "none"
  | "word-highlight"
  | "word-by-word"
  | "karaoke"
  | "bounce"
  | "typewriter";

export interface SubtitleWord {
  readonly text: string;
  readonly startTime: number;
  readonly endTime: number;
}

export interface Subtitle {
  readonly id: string;
  readonly text: string;
  readonly startTime: number;
  readonly endTime: number;
  readonly style?: SubtitleStyle;
  readonly words?: SubtitleWord[];
  readonly animationStyle?: CaptionAnimationStyle;
}

export interface SubtitleStyle {
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly color: string;
  readonly backgroundColor: string;
  readonly position: "top" | "center" | "bottom";
  readonly highlightColor?: string;
  readonly upcomingColor?: string;
}

export interface AutomationPoint {
  readonly time: number;
  readonly value: number;
}
